import StringArt from '../infra/StringArt';
import Circle, { CircleConfig } from '../shapes/Circle';
import Color from '../helpers/color/Color';
import { ColorConfig } from '../helpers/color/color.types';
import { ControlsConfig } from '../types/config.types';
import { CalcOptions } from '../types/stringart.types';
import Controller from '../infra/Controller';
import Nails from '../infra/nails/Nails';

type SpreadModeType = 'evenly' | 'distance';

interface CometConfig extends CircleConfig, ColorConfig {
  layers: number;
  ringSize: number;
  layerSpread: SpreadModeType;
  layerDistance: number;
  colorPerLayer: boolean;
}

interface SpreadMode {
  f: (layerIndex: number, config: CometConfig) => number;
  name: string;
}

const spreadModes: Record<SpreadModeType, SpreadMode> = {
  evenly: {
    f: (layerIndex, { ringSize, layers, n }) => {
      const firstLayerDistance = Math.floor(n * ringSize);
      return Math.floor(((layers - layerIndex) * firstLayerDistance) / layers);
    },
    name: 'Evenly',
  },
  distance: {
    f: (layerIndex, { n, ringSize, layerDistance }) => {
      const firstLayerDistance = Math.floor(n * ringSize);

      if (layerIndex > 0) {
        return firstLayerDistance - layerIndex * layerDistance;
      }

      return firstLayerDistance;
    },
    name: 'Specific distance',
  },
};

const COLOR_CONFIG = Color.getConfig({
  defaults: {
    isMultiColor: true,
    color: '#ff0000',
    multicolorRange: 133,
    multicolorStart: 239,
    multicolorByLightness: false,
    minLightness: 30,
    maxLightness: 70,
    colorCount: 4,
  },
  customControls: [
    {
      key: 'colorPerLayer',
      label: 'Color per layer',
      defaultValue: true,
      type: 'checkbox',
      affectsNails: false,
    },
  ],
});

type TCalc = {
  circle: Circle;
};

export default class Comet extends StringArt<CometConfig, TCalc> {
  static type = 'comet';

  name = 'Comet';
  id = 'comet';
  controls: ControlsConfig<CometConfig> = [
    Circle.nailsConfig,
    {
      key: 'layers',
      label: 'Layers',
      defaultValue: 5,
      type: 'range',
      attr: {
        min: 1,
        max: 20,
        step: 1,
      },
      isStructural: true,
    },
    {
      key: 'ringSize',
      label: 'First layer size',
      description:
        'Nail count from the top center nail to the first connected nail in the first layer',
      defaultValue: 0.3,
      type: 'range',
      attr: {
        min: 0,
        max: 1,
        step: 0.01,
      },
      displayValue: ({ ringSize, n }) => Math.floor(n * ringSize),
      isStructural: true,
      affectsNails: false,
    },
    {
      key: 'layerSpread',
      label: 'Layer Spread',
      type: 'select',
      defaultValue: 'distance',
      options: Object.entries(spreadModes).map(([key, { name }]) => ({
        value: key,
        label: name,
      })),
      isStructural: true,
      affectsNails: false,
    },
    {
      key: 'layerDistance',
      label: 'Layer Distance',
      type: 'range',
      attr: {
        min: 1,
        max: ({ n, layers }) => Math.floor(n / 2 / layers),
        step: 1,
      },
      defaultValue: 1,
      isStructural: true,
      show: ({ layerSpread }) => layerSpread !== 'evenly',
      affectsNails: false,
    },
    Circle.rotationConfig,
    Circle.distortionConfig,
    Circle.displacementConfig,
    COLOR_CONFIG,
  ];

  defaultValues: Partial<CometConfig> = {
    n: 51,
    layers: 11,
    colorPerLayer: true,
    multicolorRange: 203,
    multicolorStart: 137,
    ringSize: 0.47,
    rotation: 90 / 360,
    distortion: 0.38,
    displacementFunc: 'fastInOut',
    displacementMag: 1.8,
    displacementFastArea: 0.43,
    layerSpread: 'distance',
    layerDistance: 1,
  };

  getCalc(options: CalcOptions): TCalc {
    const circleConfig = {
      size: options.size,
      n: this.config.n,
      margin: this.config.margin,
      rotation: this.config.rotation,
      distortion: this.config.distortion,
      displacementFunc: this.config.displacementFunc,
      displacementMag: this.config.displacementMag,
      displacementFastArea: this.config.displacementFastArea,
    };

    return {
      circle: new Circle(circleConfig),
    };
  }

  initColor(): Color {
    const { isMultiColor, colorCount, layers, colorPerLayer } = this.config;
    const realColorCount = isMultiColor
      ? colorPerLayer
        ? layers
        : Math.min(colorCount, layers)
      : 1;

    return new Color({
      ...this.config,
      isMultiColor,
      colorCount: realColorCount,
    });
  }

  getAspectRatio(calcOptions: CalcOptions): number {
    const calc = this.getCalc(calcOptions);
    return calc.circle.getAspectRatio();
  }

  getLayerRingDistance(layerIndex: number): number {
    const spread = spreadModes[this.config.layerSpread];
    if (!spread) {
      throw new Error(`Invalid spread mode, "${this.config.layerSpread}"!`);
    }

    return Math.max(1, spread.f(layerIndex, this.config));
  }

  getLayerRingStepCount(layerIndex: number): number {
    const layerRingDistance = this.getLayerRingDistance(layerIndex);
    return (this.config.n - layerRingDistance + 1) * 2 - 1;
  }

  *#drawLayer(controller: Controller, layerIndex = 0): Generator<void> {
    const { n } = this.config;
    const ringDistance = this.getLayerRingDistance(layerIndex);
    const stepCount = n - ringDistance + 1;
    let prevPointIndex = 0;
    controller.startLayer({
      color: this.color.getColor(layerIndex),
      name: String(layerIndex),
    });
    controller.goto(0);

    for (let i = 0; i <= n - ringDistance; i++) {
      controller.stringTo((i + ringDistance) % n);
      yield;

      if (i !== stepCount - 1) {
        prevPointIndex = i + 1;
        controller.stringTo(prevPointIndex);

        yield;
      }
    }
  }

  *drawStrings(controller: Controller) {
    for (let layer = 0; layer < this.config.layers; layer++) {
      yield* this.#drawLayer(controller, layer);
    }
  }

  getStepCount(): number {
    if (this.stepCount) {
      return this.stepCount;
    }

    const { layers } = this.config;
    return new Array(layers)
      .fill(0)
      .reduce(
        (totalStepCount, _, layerIndex) =>
          totalStepCount + this.getLayerRingStepCount(layerIndex),
        0
      );
  }

  getNailCount(): number {
    return this.config.n;
  }

  getNails(precision?: number): Nails {
    return this.calc.circle.getNails(precision);
  }

  thumbnailConfig = ({ n, layers }) => ({
    n: Math.min(n, n % 2 ? 29 : 28),
    layers: Math.min(layers, 8),
  });

  testStepCountConfig: Partial<CometConfig>[] = [
    {
      n: 28,
      layers: 8,
      ringSize: 0.46,
      layerSpread: 'distance',
      layerDistance: 2,
      distortion: -0.33,
    },
  ];
}
